import React, { useState } from 'react';
import axios from 'axios';
import { X, Save } from 'lucide-react';
import Button from './Button';
import { useToast } from '../context/ToastContext';
import { NTRP_RATINGS, formatNtrpRating } from '../utils/ntrpRatings';
import { getDisplayName } from '../utils/displayName';

interface UserSearchResult {
  id: number;
  name: string;
  display_name?: string;
  tennisrecord_name?: string | null;
  email: string;
  role: string;
}

interface AddMemberForm {
  email: string;
  name: string;
  is_captain: boolean;
  gender?: 'male' | 'female' | null;
  ntrp_rating?: number | null;
}

interface AddTeamMemberProps {
  teamId: number;
  onMemberAdded?: () => void;
  showCreateForm?: boolean;
  onClose?: () => void;
  allowCaptainSelection?: boolean;
}

const AddTeamMember: React.FC<AddTeamMemberProps> = ({
  teamId,
  onMemberAdded,
  showCreateForm: initialShowCreateForm = false,
  onClose,
  allowCaptainSelection = false,
}) => {
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [addingUserIds, setAddingUserIds] = useState<Set<number>>(new Set());
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(initialShowCreateForm);
  const [addMemberForm, setAddMemberForm] = useState<AddMemberForm>({
    email: '',
    name: '',
    is_captain: false,
    gender: null,
    ntrp_rating: null,
  });

  const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Only search if query contains @ and is at least 2 characters
    if (query.length < 2 || !query.includes('@')) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(
        `/teams/${teamId}/members/search`,
        { params: { query: query.trim() } }
      );
      setSearchResults(response.data);
    } catch (error) {
      console.error('Failed to search users:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleAddUser = async (user: UserSearchResult) => {
    if (addingUserIds.has(user.id) || submitting) {
      return;
    }

    setAddingUserIds(prev => new Set(prev).add(user.id));
    try {
      await axios.post(
        `/teams/${teamId}/members/${user.id}`,
        null,
        { params: { is_captain: false } }
      );
      
      toast.success(`${getDisplayName(user)} added to team!`);
      
      // Remove user from search results
      setSearchResults(prev => prev.filter(u => u.id !== user.id));
      
      if (onMemberAdded) {
        onMemberAdded();
      }
    } catch (error: any) {
      console.error(`Failed to add ${getDisplayName(user)}:`, error);
      const errorMsg = error.response?.data?.detail || `Failed to add ${getDisplayName(user)}`;
      toast.error(errorMsg);
    } finally {
      setAddingUserIds(prev => {
        const next = new Set(prev);
        next.delete(user.id);
        return next;
      });
    }
  };

  const handleAddMemberFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setAddMemberForm(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === 'gender') {
      setAddMemberForm(prev => ({
        ...prev,
        [name]: value === '' ? null : (value as 'male' | 'female' | null)
      }));
    } else {
      setAddMemberForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleCreateNewUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!addMemberForm.name || !addMemberForm.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        email: addMemberForm.email.trim() || null,
        name: addMemberForm.name.trim(),
        is_captain: addMemberForm.is_captain,
        gender: addMemberForm.gender || null,
        ntrp_rating: addMemberForm.ntrp_rating || null,
      };

      await axios.post(
        `/teams/${teamId}/members/by-email`,
        payload
      );
      
      toast.success('Member created and added to team!');
      
      // Reset form
      setAddMemberForm({
        email: '',
        name: '',
        is_captain: false,
        gender: null,
      });
      setShowCreateForm(false);
      setSearchQuery('');
      setSearchResults([]);
      
      if (onMemberAdded) {
        onMemberAdded();
      }
    } catch (error: any) {
      console.error('Failed to create member:', error);
      
      let errorMsg = 'Failed to create member';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        if (Array.isArray(detail)) {
          errorMsg = detail.map((err: any) => {
            const field = err.loc?.join('.') || 'field';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        }
      }
      
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base md:text-lg font-medium text-gray-900 dark:text-gray-100">Add Team Member</h3>
        {onClose && (
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {!showCreateForm ? (
        <>
          {/* Search for existing users */}
          <div className="space-y-4">
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Add a player
              </label>
              <input
                type="text"
                id="search"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Enter email address..."
                className="block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Type an email address to add a team member.
              </p>
            </div>

            {/* Search Results */}
            {searching && (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400"></div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Searching...</p>
              </div>
            )}

            {!searching && searchResults.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Search results:</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {searchResults.map((user) => {
                    const isAdding = addingUserIds.has(user.id);
                    return (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 border-gray-300 dark:border-gray-600 transition-colors"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 dark:text-gray-100">{getDisplayName(user)}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{user.email || 'No email'}</p>
                        </div>
                        <Button
                          type="button"
                          onClick={() => handleAddUser(user)}
                          disabled={isAdding || submitting}
                          variant="primary"
                          size="sm"
                        >
                          {isAdding ? (
                            <>
                              <div className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                              Adding...
                            </>
                          ) : (
                            <>Add</>
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!searching && searchQuery.length >= 2 && searchQuery.includes('@') && searchResults.length === 0 && (
              <div className="text-center py-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">No players found</p>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(true);
                    const isEmailFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(searchQuery.trim());
                    setAddMemberForm({ 
                      email: isEmailFormat ? searchQuery.trim() : '', 
                      name: isEmailFormat ? '' : searchQuery, 
                      is_captain: false, 
                      gender: null 
                    });
                  }}
                  className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                >
                  Create New Player
                </button>
              </div>
            )}

            {/* Create New User Button */}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setShowCreateForm(true)}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
              >
                + Create a new player
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Create New User Form */}
          <form onSubmit={handleCreateNewUser} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={addMemberForm.name}
                onChange={handleAddMemberFormChange}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor="gender" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Gender
              </label>
              <select
                id="gender"
                name="gender"
                value={addMemberForm.gender || ''}
                onChange={handleAddMemberFormChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
              >
                <option value="">Not set</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            <div>
              <label htmlFor="ntrpRating" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                NTRP Rating
              </label>
              <select
                id="ntrpRating"
                name="ntrp_rating"
                value={addMemberForm.ntrp_rating || ''}
                onChange={(e) => setAddMemberForm(prev => ({ ...prev, ntrp_rating: e.target.value === '' ? null : parseFloat(e.target.value) }))}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
              >
                <option value="">Not set</option>
                {NTRP_RATINGS.map((rating) => (
                  <option key={rating} value={rating}>
                    {formatNtrpRating(rating)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email Address <span className="text-gray-500 dark:text-gray-400 text-xs">(optional)</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={addMemberForm.email}
                onChange={handleAddMemberFormChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
                placeholder="member@example.com (optional)"
              />
            </div>

            {allowCaptainSelection && (
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_captain"
                    checked={addMemberForm.is_captain}
                    onChange={handleAddMemberFormChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Make this member a captain</span>
                </label>
              </div>
            )}

            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <Button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setAddMemberForm({ email: '', name: '', is_captain: false, gender: null, ntrp_rating: null });
                }}
                variant="outline"
                size="lg"
              >
                <X className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                variant="primary"
                size="lg"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Create & Add Member
                  </>
                )}
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default AddTeamMember;
